/**
 * `/checkout` feature slice.
 *
 * Owns the lifecycle for a single pay-now flow:
 *   loadCart      → BFF `GET /v1/checkout`
 *   createSession → BFF `POST /v1/checkout/payment-session`
 *   confirmOrder  → BFF `POST /v1/checkout/confirm`
 *
 * Errors surface to the UI as `{ code, message }` so the status / error
 * pages can render a precise reason (e.g. `invalid_request`, `cart_not_found`).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { BffError, checkoutApi } from '../../services';
import type {
  CheckoutConfirmRequestBody,
  CheckoutConfirmResponse,
  CheckoutCurrentResponse,
  PaymentSessionRequestBody,
  PaymentSessionResponse,
} from '../../types/api';

export interface CheckoutSliceError {
  code: string;
  message: string;
}

export interface CheckoutState {
  cart: CheckoutCurrentResponse | null;
  session: PaymentSessionResponse | null;
  order: CheckoutConfirmResponse | null;
  loading: boolean;
  error: CheckoutSliceError | null;
}

const initialState: CheckoutState = {
  cart: null,
  session: null,
  order: null,
  loading: false,
  error: null,
};

function asSliceError(error: unknown): CheckoutSliceError {
  if (error instanceof BffError) return { code: error.code, message: error.message };
  if (error instanceof Error) return { code: 'unknown_error', message: error.message };
  return { code: 'unknown_error', message: 'Unknown error' };
}

export const loadCart = createAsyncThunk<
  CheckoutCurrentResponse,
  { tokenId: string; countryCode: string },
  { rejectValue: CheckoutSliceError }
>('checkout/loadCart', async (input, { rejectWithValue }) => {
  try {
    return await checkoutApi.getCurrentCart(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

export const createPaymentSession = createAsyncThunk<
  PaymentSessionResponse,
  { tokenId: string; countryCode: string; body: PaymentSessionRequestBody },
  { rejectValue: CheckoutSliceError }
>('checkout/createPaymentSession', async (input, { rejectWithValue }) => {
  try {
    return await checkoutApi.createPaymentSession(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

export const confirmOrder = createAsyncThunk<
  CheckoutConfirmResponse,
  {
    tokenId: string;
    countryCode: string;
    checkoutToken: string;
    body: CheckoutConfirmRequestBody;
  },
  { rejectValue: CheckoutSliceError }
>('checkout/confirmOrder', async (input, { rejectWithValue }) => {
  try {
    return await checkoutApi.confirm(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    resetCheckout(): CheckoutState {
      return initialState;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCart.fulfilled, (state, action: PayloadAction<CheckoutCurrentResponse>) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(loadCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? { code: 'unknown_error', message: 'loadCart failed' };
      })
      .addCase(createPaymentSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createPaymentSession.fulfilled,
        (state, action: PayloadAction<PaymentSessionResponse>) => {
          state.loading = false;
          state.session = action.payload;
        },
      )
      .addCase(createPaymentSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'createPaymentSession failed',
        };
      })
      .addCase(confirmOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmOrder.fulfilled, (state, action: PayloadAction<CheckoutConfirmResponse>) => {
        state.loading = false;
        state.order = action.payload;
      })
      .addCase(confirmOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'confirmOrder failed',
        };
      });
  },
});

export const { resetCheckout, clearError } = checkoutSlice.actions;
export default checkoutSlice.reducer;
